import type {
  Enrollment,
  EnrollmentCourseStructure,
  EnrollmentProgress,
  PreviewCourseStructure,
} from "@/api/generated/model";
import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import { CourseTreeNodeFromJSON } from "@/types/generated";
import { create } from "zustand";
import {
  getEnrollment,
  getEnrollmentCourseStructure,
} from "@/api/generated/enrollments/enrollments";
import { getPreviewCourseStructure } from "@/api/generated/preview/preview";
import {
  listPreviewSubChapterStoryboards,
  listSubChapterStoryboards,
} from "@/api/storyboards";
import { errorCodeOf } from "@/api/apiFetch";
import {
  LessonStatus,
  lessonStatusFromApi,
} from "@/components/course/tree/types";
import type { Storyboard } from "@/types/storyboard";
import {
  EMPTY_NODE_INDEX_CONTEXT,
  nextNodeContext,
  type NodeIndexContext,
  type SiblingCounters,
} from "@/lib/nodeContext";

interface NodeIndex {
  node: CourseTreeNode;
  module: CourseTreeNode | null;
  chapter: CourseTreeNode | null;
  subchapter: CourseTreeNode | null;
  parent: CourseTreeNode | null;
  // Only set for Subchapter entries — the (recursive) list of CEs under that
  // subchapter, used by `selectedContentElements` to render storyboards.
  contentElements?: CourseTreeNode[];
  // Display position of this entry's Module / Chapter ancestor among its
  // siblings (counting from 1, not 0 — these go straight into the UI as
  // "Modul 2", "Kap. 3"). `chapterIndex` is null on Module entries.
  moduleIndex: number | null;
  chapterIndex: number | null;
  // Display position of this entry within its subchapter's content elements
  // (counting from 1) and the total count. Only set on ContentElement
  // entries; null otherwise.
  contentElementIndex: number | null;
  contentElementTotal: number | null;
}

const collectContentElements = (node: CourseTreeNode): CourseTreeNode[] => {
  if (node.type === CourseTreeNodeTypeEnum.ContentElement) return [node];
  return (node.children ?? []).flatMap(collectContentElements);
};

const contentElementPosition = (
  node: CourseTreeNode,
  subchapter: CourseTreeNode | null
): { index: number; total: number } | null => {
  if (node.type !== CourseTreeNodeTypeEnum.ContentElement || !subchapter) {
    return null;
  }
  const ces = collectContentElements(subchapter);
  const idx = ces.findIndex((c) => c.id === node.id);
  return idx < 0 ? null : { index: idx + 1, total: ces.length };
};

const buildNodeIndex = (
  nodes: unknown[],
  index: Map<string, NodeIndex> = new Map(),
  context: NodeIndexContext = EMPTY_NODE_INDEX_CONTEXT
): Map<string, NodeIndex> => {
  // Counters are local to this recursion level: top-level counts modules,
  // inside a module's children counts chapters. Threaded immutably across
  // each iteration via nextNodeContext's return value.
  let counters: SiblingCounters = { module: 0, chapter: 0 };

  for (const raw of nodes) {
    const node = CourseTreeNodeFromJSON(raw);
    const { context: next, counters: nextCounters } = nextNodeContext(
      node,
      context,
      counters
    );
    counters = nextCounters;

    const cePosition = contentElementPosition(node, next.subchapter);
    const contentElements =
      node.type === CourseTreeNodeTypeEnum.Subchapter
        ? collectContentElements(node)
        : undefined;

    index.set(node.id, {
      node,
      module: next.module,
      chapter: next.chapter,
      subchapter: next.subchapter,
      parent: context.parent,
      contentElements,
      moduleIndex: next.moduleIndex,
      chapterIndex: next.chapterIndex,
      contentElementIndex: cePosition?.index ?? null,
      contentElementTotal: cePosition?.total ?? null,
    });

    if (node.children) {
      buildNodeIndex(node.children, index, { ...next, parent: node });
    }
  }
  return index;
};

type ViewMode = "intro" | "outro" | "content";

interface CourseDetailState {
  enrollmentId: string | null;
  trainerMode: boolean;
  enrollment: Enrollment | null;
  courseStructureId: string | null;
  courseTitle: string | null;
  courseData: LoadedCourse | null;
  /** Factory course category (`code`, `methods`, …). Null until a payload includes one. */
  courseCategory: string | null;
  nodeIndex: Map<string, NodeIndex>;
  selectedNodeId: string | null;
  focusedNodeId: string | null;
  selectedNodeContent: CourseTreeNode | null;
  selectedContentElements: CourseTreeNode[];
  parentChapter: CourseTreeNode | null;
  parentModule: CourseTreeNode | null;
  viewMode: ViewMode;
  expandedChapters: Set<string>;
  expandedModules: Set<string>;
  loading: boolean;
  error: string | null;
  progressPercent: number;
  progressSubchapters: Record<
    string,
    {
      status: LessonStatus;
      progressPercent: number;
    }
  >;
  progressNextLessonId: string | null;
  completedContentElementIds: Set<string>;
  storyboards: Storyboard[];
  storyboardSubChapterId: string | null;
  storyboardsLoading: boolean;

  fetchCourse: (enrollmentId: string) => Promise<void>;
  fetchCourseByExternalId: (courseExternalId: string) => Promise<void>;
  fetchTrainerPreviewCourse: (enrollmentId: string) => Promise<void>;
  fetchStoryboardsBySubChapter: (subChapterId: string) => Promise<Storyboard[]>;
  refreshProgress: () => Promise<void>;
  selectNode: (nodeId: string | null) => void;
  setFocusedNodeId: (nodeId: string | null) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleChapter: (chapterId: string) => void;
  expandChapter: (chapterId: string) => void;
  expandChapters: (chapterIds: string[]) => void;
  resetExpandedChapters: () => void;
  toggleModule: (moduleId: string) => void;
  expandModule: (moduleId: string) => void;
  reset: () => void;
}

const initialState = {
  enrollmentId: null,
  trainerMode: false,
  enrollment: null as Enrollment | null,
  courseStructureId: null,
  courseTitle: null,
  courseData: null,
  courseCategory: null as string | null,
  nodeIndex: new Map<string, NodeIndex>(),
  selectedNodeId: null,
  focusedNodeId: null as string | null,
  selectedNodeContent: null,
  selectedContentElements: [] as CourseTreeNode[],
  parentChapter: null,
  parentModule: null,
  viewMode: "content" as ViewMode,
  expandedChapters: new Set<string>(),
  expandedModules: new Set<string>(),
  resetExpandedChapters: () => {},
  loading: false,
  error: null,
  progressPercent: 0,
  progressSubchapters: {} as Record<
    string,
    {
      status: LessonStatus;
      progressPercent: number;
    }
  >,
  progressNextLessonId: null,
  completedContentElementIds: new Set<string>(),
  storyboards: [],
  storyboardSubChapterId: null,
  storyboardsLoading: false,
};

/** The course payload of either source: the enrollment's snapshot or the live preview. */
type CoursePayload = EnrollmentCourseStructure | PreviewCourseStructure;

/** The course as the store holds it, with the tree never missing. */
export interface LoadedCourse {
  title: string;
  intro: string | null;
  structure: CourseTreeNode[];
}

export const toLoadedCourse = (data: CoursePayload): LoadedCourse => ({
  title: data.title,
  intro: data.intro ?? null,
  structure: data.structure ?? [],
});

/** Blank and missing categories stay null so a previous course cannot leak through. */
const courseCategoryFromPayload = (data: CoursePayload): string | null => {
  const category = data.category?.trim();
  if (!category) return null;
  return category;
};

/** The learner's progress as the store keeps it; empty when there is none yet. */
const progressFieldsFrom = (progressData: EnrollmentProgress | null) => ({
  progressPercent: progressData?.progressPercent ?? 0,
  progressSubchapters: Object.fromEntries(
    (progressData?.subchapters ?? []).map((s) => [
      s.id,
      {
        status: lessonStatusFromApi(s.status),
        progressPercent: s.progressPercent,
      },
    ])
  ),
  progressNextLessonId: progressData?.nextLessonId ?? null,
  completedContentElementIds: new Set(
    progressData?.completedContentElementIds ?? []
  ),
});

export const useCourseDetailStore = create<CourseDetailState>((set, get) => {
  const applyLoadedCourse = (
    data: CoursePayload,
    courseExternalId: string,
    progressData: EnrollmentProgress | null,
    isPreview = false
  ) => {
    const course = toLoadedCourse(data);
    const nodeIndex = buildNodeIndex(course.structure);

    // Auto-select the first available subchapter entry (Module → Chapter → Subchapter)
    const firstModule = course.structure[0];
    const firstChapter = firstModule?.children?.[0];
    const firstSubchapter = firstChapter?.children?.[0];
    const firstEntry = firstSubchapter
      ? nodeIndex.get(firstSubchapter.id)
      : null;

    // Show course intro on first entry (no progress yet), but never in preview mode
    const isFirstEntry =
      !isPreview &&
      (progressData === null || progressData.progressPercent === 0);

    const courseCategory = courseCategoryFromPayload(data);
    const progressFields = progressFieldsFrom(progressData);

    if (isFirstEntry) {
      set({
        courseStructureId: courseExternalId,
        courseTitle: course.title,
        courseData: course,
        courseCategory,
        nodeIndex,
        loading: false,
        expandedModules: new Set(),
        expandedChapters: new Set(),
        selectedNodeId: null,
        selectedNodeContent: null,
        selectedContentElements: [],
        parentChapter: null,
        parentModule: null,
        viewMode: "intro" as ViewMode,
        ...progressFields,
      });
      return;
    }

    set({
      courseStructureId: courseExternalId,
      courseTitle: course.title,
      courseData: course,
      courseCategory,
      nodeIndex,
      loading: false,
      expandedModules: firstModule ? new Set([firstModule.id]) : new Set(),
      expandedChapters: firstChapter ? new Set([firstChapter.id]) : new Set(),
      selectedNodeId: firstSubchapter?.id ?? null,
      selectedNodeContent: firstEntry?.node ?? null,
      selectedContentElements: firstEntry?.contentElements ?? [],
      parentChapter: firstEntry?.chapter ?? null,
      parentModule: firstEntry?.module ?? null,
      viewMode: "content" as ViewMode,
      ...progressFields,
    });

    if (firstSubchapter?.id) {
      const entry = get().nodeIndex.get(firstSubchapter.id);
      if (entry?.node.type === CourseTreeNodeTypeEnum.Subchapter) {
        get().fetchStoryboardsBySubChapter(firstSubchapter.id);
      }
    }
  };

  return {
    ...initialState,

    fetchCourse: async (enrollmentId: string) => {
      set({ loading: true, error: null, enrollmentId, trainerMode: false });
      try {
        const enrollment = await getEnrollment(enrollmentId);
        set({ enrollment, courseTitle: enrollment.courseTitle });
        const data = await getEnrollmentCourseStructure(enrollmentId);
        applyLoadedCourse(
          data,
          enrollment.courseExternalId,
          enrollment.progress ?? null
        );
      } catch (err) {
        set({
          error: err instanceof Error ? err.message : "Failed to load course",
          loading: false,
        });
      }
    },

    fetchCourseByExternalId: async (courseExternalId: string) => {
      if (get().courseStructureId === courseExternalId && get().courseData)
        return;
      set({
        loading: true,
        error: null,
        enrollmentId: null,
        trainerMode: false,
      });
      try {
        const data = await getPreviewCourseStructure(courseExternalId);
        applyLoadedCourse(data, courseExternalId, null, true);
      } catch (err) {
        // The page tells a missing admin role apart from other failures by the
        // backend's error code.
        set({
          error:
            errorCodeOf(err) ??
            (err instanceof Error ? err.message : "Failed to load course"),
          loading: false,
        });
      }
    },

    fetchTrainerPreviewCourse: async (enrollmentId: string) => {
      set({ loading: true, error: null, enrollmentId, trainerMode: true });
      try {
        const enrollment = await getEnrollment(enrollmentId);
        set({ enrollment, courseTitle: enrollment.courseTitle });
        const data = await getEnrollmentCourseStructure(enrollmentId);
        applyLoadedCourse(data, enrollment.courseExternalId, null);
      } catch (err) {
        set({
          error: err instanceof Error ? err.message : "Failed to load course",
          loading: false,
        });
      }
    },

    fetchStoryboardsBySubChapter: async (subChapterId: string) => {
      // return early if the subchapter has already been requested
      if (get().storyboardSubChapterId === subChapterId)
        return get().storyboards;
      set({
        storyboardSubChapterId: subChapterId,
        storyboardsLoading: true,
        storyboards: [],
      });
      const isPreview = get().enrollmentId === null;
      try {
        const storyboards = isPreview
          ? await listPreviewSubChapterStoryboards(subChapterId)
          : await listSubChapterStoryboards(subChapterId);
        // Discard result if another subchapter was requested while this was in-flight
        if (get().storyboardSubChapterId !== subChapterId) return [];
        set({ storyboards, storyboardsLoading: false });
        return storyboards;
      } catch (err) {
        console.error(
          "Failed to fetch storyboards for subchapter",
          subChapterId,
          err
        );
        if (get().storyboardSubChapterId === subChapterId) {
          set({ storyboardsLoading: false });
        }
        return [];
      }
    },

    refreshProgress: async () => {
      const { enrollmentId } = useCourseDetailStore.getState();
      if (!enrollmentId) return;
      const progressData = await getEnrollment(enrollmentId)
        .then((enrollment) => enrollment.progress)
        .catch(() => null);
      if (!progressData) return;

      set(progressFieldsFrom(progressData));
    },

    selectNode: (nodeId: string | null) => {
      set((state) => {
        if (!nodeId) {
          return {
            selectedNodeId: null,
            focusedNodeId: null,
            selectedNodeContent: null,
            selectedContentElements: [],
            parentChapter: null,
            parentModule: null,
            viewMode: "content" as ViewMode,
          };
        }

        const entry = state.nodeIndex.get(nodeId);
        const nodeType = entry?.node.type;
        const viewMode: ViewMode =
          nodeType === CourseTreeNodeTypeEnum.Module ||
          nodeType === CourseTreeNodeTypeEnum.Chapter
            ? "intro"
            : "content";
        // Re-selecting the same chapter (unveil, progress refresh) must not
        // drop the first-subchapter highlight the intro stack just applied.
        const selectionChanged = state.selectedNodeId !== nodeId;

        return {
          selectedNodeId: nodeId,
          focusedNodeId: selectionChanged ? null : state.focusedNodeId,
          selectedNodeContent: entry?.node ?? null,
          selectedContentElements: entry?.contentElements ?? [],
          parentChapter: entry?.chapter ?? null,
          parentModule: entry?.module ?? null,
          viewMode,
        };
      });

      if (nodeId) {
        const state = get();
        const entry = state.nodeIndex.get(nodeId);
        if (entry?.node.type === CourseTreeNodeTypeEnum.Subchapter) {
          get().fetchStoryboardsBySubChapter(nodeId);
        }
      }
    },

    setFocusedNodeId: (nodeId: string | null) => set({ focusedNodeId: nodeId }),

    setViewMode: (mode: ViewMode) => set({ viewMode: mode }),

    toggleChapter: (chapterId: string) =>
      set((state) => {
        const newSet = new Set(state.expandedChapters);
        if (newSet.has(chapterId)) {
          newSet.delete(chapterId);
        } else {
          newSet.add(chapterId);
        }
        return { expandedChapters: newSet };
      }),

    expandChapter: (chapterId: string) =>
      set((state) => {
        const newSet = new Set(state.expandedChapters);
        newSet.add(chapterId);
        return { expandedChapters: newSet };
      }),

    expandChapters: (chapterIds: string[]) =>
      set((state) => {
        const newSet = new Set(state.expandedChapters);
        chapterIds.forEach((id) => newSet.add(id));
        return { expandedChapters: newSet };
      }),

    resetExpandedChapters: () => set(() => ({ expandedChapters: new Set() })),

    toggleModule: (moduleId: string) =>
      set((state) => {
        const newSet = new Set(state.expandedModules);
        if (newSet.has(moduleId)) {
          newSet.delete(moduleId);
        } else {
          newSet.add(moduleId);
        }
        return { expandedModules: newSet };
      }),

    expandModule: (moduleId: string) =>
      set((state) => {
        const newSet = new Set(state.expandedModules);
        newSet.add(moduleId);
        return { expandedModules: newSet };
      }),

    reset: () => set(initialState),
  };
});
